// The launch popup's decision, separated from the component that renders it.
//
// The whole feature is one question asked once: does this signed-in person have
// a launch report waiting? Getting it wrong in either direction is expensive - a
// popup that fires twice is a nag, and one that marks itself "shown" while the
// run was still publishing is a report nobody is ever told about. So the rules
// live here as a pure decision over the two RPCs, and the component only draws
// the answer.
//
// Vocabulary: upcoming / unreleased, never "spoiled".

import {
  claimLaunch as defaultClaimLaunch,
  claimStatus as defaultClaimStatus,
  claimToken as defaultClaimToken,
  clearClaimToken as defaultClearClaimToken,
  peekClaimToken as defaultPeekClaimToken,
  type ClaimResult,
  type ClaimStatus,
} from './watchtower'

const SHOWN_PREFIX = 'commander-bracket-watchtower-popup-shown'

/**
 * The "we have already shown this person the popup" mark.
 *
 * Keyed by (campaign, userId) exactly like the claim token: two accounts on one
 * browser are two people, and the next campaign is a new question.
 */
export function popupShownKey(campaign: string, userId: string): string {
  return `${SHOWN_PREFIX}:${campaign}:${userId}`
}

export function popupWasShown(campaign: string, userId: string): boolean {
  try {
    return localStorage.getItem(popupShownKey(campaign, userId)) === '1'
  } catch {
    // Storage unavailable (private mode). Fail towards showing: at worst the
    // popup repeats for someone who cannot persist anything anyway.
    return false
  }
}

export function markPopupShown(campaign: string, userId: string): void {
  try {
    localStorage.setItem(popupShownKey(campaign, userId), '1')
  } catch {
    // ignore
  }
}

export function clearPopupShown(campaign: string, userId: string): void {
  try {
    localStorage.removeItem(popupShownKey(campaign, userId))
  } catch {
    // ignore
  }
}

// --- the once-per-app-load guard
//
// Separate from the localStorage mark above, and deliberately in-memory: the
// mark is "this person has been told", this is "we have already asked on this
// page load". A `try_again` clears neither, so the next open asks again - which
// is the whole retry policy.
//
// The guard is the DECISION PROMISE itself, not a boolean. It used to be a
// claimed/not-claimed flag, and React StrictMode showed why that is the wrong
// shape: the effect runs, its cleanup runs (cancelling the first run), and the
// replay hit the flag and exited - so a dev build never showed the popup at
// all. Memoising the promise means every mount for the same (campaign, user)
// awaits the SAME resolution, and whichever mount is still alive when it lands
// draws it.

const decisionsThisLoad = new Map<string, Promise<PopupDecision>>()

/**
 * This app load's single resolution for (campaign, user).
 *
 * The first caller's `resolve` runs; every later caller for the same key gets
 * the same promise back and `resolve` is not called again. Keyed per user so
 * signing out and back in as somebody else asks again for the new account.
 */
export function popupDecisionOnce(
  campaign: string,
  userId: string,
  resolve: () => Promise<PopupDecision>,
): Promise<PopupDecision> {
  const key = `${campaign}:${userId}`
  const pending = decisionsThisLoad.get(key)
  if (pending) return pending
  const decision = resolve()
  decisionsThisLoad.set(key, decision)
  return decision
}

/** Test seam. A real page load starts with an empty map anyway. */
export function resetPopupAttempts(): void {
  decisionsThisLoad.clear()
}

/**
 * Is this pathname a Watchtower surface?
 *
 * The root popup asks this before it draws anything. A person who is already
 * on `/watchtower` (the archive, which is where the popup's own button goes),
 * or on `/watchtower/<report>` (the email link), is looking at the thing the
 * popup exists to announce: drawing it on top of their report is the overlay
 * the 2026-09-10 review flagged. The decision still resolves - the claim is
 * recorded and the shown mark is written, because reaching Watchtower on
 * your own is the telling - but the modal is not rendered.
 */
export function isWatchtowerRoute(pathname: string): boolean {
  return pathname === '/watchtower' || pathname.startsWith('/watchtower/')
}

/**
 * Is this a campaign link, whose PAGE makes the claim?
 *
 * `/watchtower?campaign=<id>` resolves the claim itself and navigates onto
 * whatever it returns (WatchtowerPage's campaign effect). The root popup
 * makes no claim of its own on that URL - not even a shared one - so a load
 * from a campaign link is exactly one claim, and the page writes the shown
 * mark when its claim opens a report. Mirrors the page's own condition: the
 * bare archive path with a campaign param, never a report path.
 */
export function campaignLinkOwnsClaim(pathname: string, search: string): boolean {
  // A NON-EMPTY campaign, exactly as the page tests it: `?campaign=` with no
  // value makes the page claim nothing, and if the popup yielded to it too
  // that load would make no claim at all.
  return pathname === '/watchtower' && !!new URLSearchParams(search).get('campaign')
}

/**
 * What one attempt concluded.
 *
 * `show` and `markShown` are separate because they come apart in both
 * directions: a report we show is marked, and a claim we know landed on some
 * other device is marked WITHOUT being shown. Everything else - not yet
 * published, a publish holding the row, an RPC that is not deployed - leaves no
 * mark at all, so the next open asks again.
 */
export interface PopupDecision {
  show: boolean
  reportId: string | null
  /** The active run the claim resolved against; travels on both popup events. */
  runId: string | null
  markShown: boolean
  /** Why, for tests and for the one telemetry row this can produce. */
  reason:
    | 'already_shown'
    | 'claimed'
    | 'not_yet'
    | 'try_again'
    | 'claimed_elsewhere'
    | 'token_mismatch'
    | 'bad_token'
    | 'unavailable'
    | 'error'
}

export interface PopupDeps {
  claimStatus: (campaign: string) => Promise<ClaimStatus>
  claimLaunch: (campaign: string, token: string) => Promise<ClaimResult>
  claimToken: (campaign: string, userId: string) => string
  peekClaimToken: (campaign: string, userId: string) => string | null
  clearClaimToken: (campaign: string, userId: string) => void
  wasShown: (campaign: string, userId: string) => boolean
}

const DEFAULT_DEPS: PopupDeps = {
  claimStatus: defaultClaimStatus,
  claimLaunch: defaultClaimLaunch,
  claimToken: defaultClaimToken,
  clearClaimToken: defaultClearClaimToken,
  peekClaimToken: defaultPeekClaimToken,
  wasShown: popupWasShown,
}

const NOTHING = { show: false as const, reportId: null, runId: null }

/**
 * Resolve the launch popup for one app load.
 *
 * Order is `claimStatus` then `claimLaunch`, per the spec's Claims section. The
 * status read is the cheap way to skip the write path for the overwhelming
 * majority of opens; the claim itself is what resolves WHICH report, because
 * the claim function re-resolves the caller's current visible report against
 * the active run every time it is called. That is also why an already-claimed
 * person on a device that still holds the token asks again: a claim recorded
 * before the run published returns no report, and the same token presented
 * after it publishes returns one.
 *
 * The one case that stops asking forever without showing anything is a claim we
 * cannot re-present: claimed, but this device has no token. A fresh token would
 * be rejected by design (the stored hash is never overwritten), so there is
 * nothing here to resolve and no reason to keep calling.
 *
 * Never throws. Every failure is a decision that shows nothing and marks
 * nothing.
 */
export async function resolveLaunchPopup(
  campaign: string,
  userId: string,
  deps: Partial<PopupDeps> = {},
): Promise<PopupDecision> {
  const d = { ...DEFAULT_DEPS, ...deps }
  if (!campaign || !userId) return { ...NOTHING, markShown: false, reason: 'unavailable' }
  if (d.wasShown(campaign, userId)) {
    return { ...NOTHING, markShown: false, reason: 'already_shown' }
  }

  let status: ClaimStatus
  try {
    status = await d.claimStatus(campaign)
  } catch {
    return { ...NOTHING, markShown: false, reason: 'error' }
  }
  // A missing RPC is the NORMAL state until the migration lands. It is never an
  // error a user reads, and it must never leave a mark - the popup has to still
  // be waiting when the function appears.
  if (status.status === 'unavailable') {
    return { ...NOTHING, markShown: false, reason: 'unavailable' }
  }
  if (status.status === 'error') {
    return { ...NOTHING, markShown: false, reason: 'error' }
  }
  if (status.status === 'claimed' && !d.peekClaimToken(campaign, userId)) {
    return { ...NOTHING, markShown: true, reason: 'claimed_elsewhere' }
  }

  let claim: ClaimResult
  try {
    claim = await d.claimLaunch(campaign, d.claimToken(campaign, userId))
  } catch {
    return { ...NOTHING, markShown: false, reason: 'error' }
  }

  switch (claim.status) {
    case 'claimed':
      // A `claimed` with no report id means this person has nothing visible on
      // the ACTIVE run - they had no saved deck when the population was frozen,
      // or their report is on a superseded one. Nothing to show, and nothing to
      // mark: the next run may well have one for them.
      return claim.reportId
        ? { show: true, reportId: claim.reportId, runId: claim.runId, markShown: true, reason: 'claimed' }
        : { ...NOTHING, runId: claim.runId, markShown: false, reason: 'not_yet' }
    case 'not_yet':
      // Nothing has published. No lock was taken and no claim was recorded, so
      // the token stays and the next open asks again.
      return { ...NOTHING, markShown: false, reason: 'not_yet' }
    case 'try_again':
      // A publish held the generation row. Keep the token, ask on the next
      // open, and do NOT loop here against a transaction whose job is to take
      // its time.
      return { ...NOTHING, markShown: false, reason: 'try_again' }
    case 'token_mismatch':
      // Another device's token owns this (campaign, user) and the stored hash
      // is never overwritten, so this one can never win. Stop asking.
      return { ...NOTHING, markShown: true, reason: 'token_mismatch' }
    case 'bad_token':
      // WT011: what we persisted is not a UUID. Throw it away so the next open
      // mints a valid one, and leave no mark.
      d.clearClaimToken(campaign, userId)
      return { ...NOTHING, markShown: false, reason: 'bad_token' }
    case 'unavailable':
      return { ...NOTHING, markShown: false, reason: 'unavailable' }
    default:
      return { ...NOTHING, markShown: false, reason: 'error' }
  }
}
